import type { AuthorityFieldRule } from '@vowric/shared';
import type { Ownership } from './OwnershipMark.js';

// The C3 authority manifest's `fields` section is OwnershipMark's data source
// (decision 2d089cf9); this is the mapper between them, kept here so every
// surface reads the same rule the same way instead of each screen inventing
// its own "can I edit this?" test.
//
// Type-only dependency on @vowric/shared: the manifest's shape is Core's, and
// duplicating it here would be exactly the drift the manifest exists to
// prevent. Nothing from that package survives compilation.
//
// Deliberately NOT `resolveFieldAccess` from @vowric/core (authority/
// resolve.ts). That one is the server-side display resolver: it takes a
// relation, understands "role:assigned"/"role:own" scope suffixes, and treats
// an undeclared field as fully yours because the ACTION gate already let the
// caller in. None of that holds in a browser: there is no gate above this
// call, and a scoped grant cannot be evaluated without the relation. So this
// mapper fails CLOSED at both ends —
//   • rule undefined → 'view-only', not 'you'. A missing rule in the UI means
//     "the manifest did not reach me", and the honest render of not knowing
//     who owns a field is to show it and offer the ask, never to hand over an
//     edit control that the server will then refuse.
//   • a scope-suffixed slug ("dispatcher:assigned") does not match a bare
//     actor role, so it reads as 'office' — the conservative side of a
//     question this function deliberately lacks the data to answer.
export function ownershipFromRule(
  rule: AuthorityFieldRule | undefined,
  actorRoles: string[],
): Ownership {
  if (!rule) return 'view-only';
  // visible_locked is the manifest AUTHOR saying "render it, lock it, the way
  // forward is an ask" (wf02's masked pay). It outranks the write list: a rule
  // that both masks and grants write is a contradiction, and the mask is the
  // half that was written for the reader's eyes.
  if (rule.mask === 'visible_locked') return 'view-only';
  if (rule.write === '*') return 'you';
  if (rule.write.some((role) => actorRoles.includes(role))) return 'you';
  return 'office';
}
